import { lazy } from 'react';
import accountingPath from 'src/constants/urlPath/erp/accountingPath';
import type { RouteType } from 'src/hooks/useRouteElements';
import NewLayout from '../../layouts/NewFeatures/MainLayout';
import { PermissionEn } from '@martech-solution/utils/constants';

const Debt = lazy(() => import('src/pages/Purchase/Debt'));
const Incoterm = lazy(() => import('src/pages/ERP/Accounting/Incoterm'));
const IncotermForm = lazy(() => import('src/pages/ERP/Accounting/Incoterm/IncotermForm'));
const TradePaymentMethod = lazy(() => import('src/pages/ERP/Accounting/TradePaymentMethod'));
const TradePaymentMethodForm = lazy(() => import('src/pages/ERP/Accounting/TradePaymentMethod/TradePaymentMethodForm'));
const PaymentDocument = lazy(() => import('src/pages/ERP/Accounting/PaymentDocument'));
const PaymentDocumentForm = lazy(() => import('src/pages/ERP/Accounting/PaymentDocument/PaymentDocumentForm'));
const MainShippingMethod = lazy(() => import('src/pages/ERP/Accounting/MainShippingMethod'));
const MainShippingMethodForm = lazy(() => import('src/pages/ERP/Accounting/MainShippingMethod/MainShippingMethodForm'));
const ShippingStage = lazy(() => import('src/pages/ERP/Accounting/ShippingStage'));
const ShippingStageForm = lazy(() => import('src/pages/ERP/Accounting/ShippingStage/ShippingStageForm'));
const InternationalTradeAgreement = lazy(() => import('src/pages/ERP/Accounting/InternationalTradeAgreement'));
const InternationalTradeAgreementForm = lazy(
  () => import('src/pages/ERP/Accounting/InternationalTradeAgreement/InternationalTradeAgreementForm')
);
const PaymentDocumentDetailRequirement = lazy(
  () => import('src/pages/ERP/Accounting/PaymentDocumentDetailRequirement')
);
const PaymentDocumentDetailRequirementForm = lazy(
  () => import('src/pages/ERP/Accounting/PaymentDocumentDetailRequirement/PaymentDocumentDetailRequirementForm')
);
const AssetAllocation = lazy(() => import('src/pages/ERP/Accounting/AssetAllocation'));
const AssetAllocationForm = lazy(() => import('src/pages/ERP/Accounting/AssetAllocation/AssetAllocationForm'));

const TransactionMaster = lazy(() => import('src/pages/ERP/Accounting/FinancialAccounting/TransactionMaster'));
const TransactionMasterForm = lazy(
  () => import('src/pages/ERP/Accounting/FinancialAccounting/TransactionMaster/TransactionMasterForm')
);

const DocumentLog = lazy(() => import('src/pages/ERP/Accounting/FinancialAccounting/DocumentLog'));

const accountingRoutes: RouteType[] = [
  // Incoterm

  {
    path: accountingPath.incoterm,
    Layout: NewLayout,
    Component: Incoterm,
    permission: PermissionEn.read
  },
  {
    path: accountingPath.incotermAdd,
    Layout: NewLayout,
    Component: IncotermForm,
    checkPath: accountingPath.incoterm,
    permission: PermissionEn.create
  },
  {
    path: `${accountingPath.incotermEdit}/:id`,
    Layout: NewLayout,
    Component: IncotermForm,
    checkPath: accountingPath.incoterm,
    permission: PermissionEn.update
  },
  {
    path: `${accountingPath.incotermDetail}/:id`,
    Layout: NewLayout,
    Component: IncotermForm,
    checkPath: accountingPath.incoterm,
    permission: PermissionEn.viewDetail
  },

  // Payment Method
  {
    path: accountingPath.tradePaymentMethod,
    Layout: NewLayout,
    Component: TradePaymentMethod,
    permission: PermissionEn.read
  },
  {
    path: accountingPath.tradePaymentMethodAdd,
    Layout: NewLayout,
    Component: TradePaymentMethodForm,
    checkPath: accountingPath.tradePaymentMethod,
    permission: PermissionEn.create
  },
  {
    path: `${accountingPath.tradePaymentMethodEdit}/:id`,
    Layout: NewLayout,
    Component: TradePaymentMethodForm,
    checkPath: accountingPath.tradePaymentMethod,
    permission: PermissionEn.update
  },
  {
    path: `${accountingPath.tradePaymentMethodDetail}/:id`,
    Layout: NewLayout,
    Component: TradePaymentMethodForm,
    checkPath: accountingPath.tradePaymentMethod,
    permission: PermissionEn.viewDetail
  },

  // Payment Document
  {
    path: accountingPath.paymentDocument,
    Layout: NewLayout,
    Component: PaymentDocument,
    permission: PermissionEn.read
  },
  {
    path: accountingPath.paymentDocumentAdd,
    Layout: NewLayout,
    Component: PaymentDocumentForm,
    checkPath: accountingPath.paymentDocument,
    permission: PermissionEn.create
  },
  {
    path: `${accountingPath.paymentDocumentEdit}/:id`,
    Layout: NewLayout,
    Component: PaymentDocumentForm,
    checkPath: accountingPath.paymentDocument,
    permission: PermissionEn.update
  },
  {
    path: `${accountingPath.paymentDocumentDetail}/:id`,
    Layout: NewLayout,
    Component: PaymentDocumentForm,
    checkPath: accountingPath.paymentDocument,
    permission: PermissionEn.viewDetail
  },

  // Main shipping method
  {
    path: accountingPath.mainShippingMethod,
    Layout: NewLayout,
    Component: MainShippingMethod,
    permission: PermissionEn.read
  },
  {
    path: accountingPath.mainShippingMethodAdd,
    Layout: NewLayout,
    Component: MainShippingMethodForm,
    checkPath: accountingPath.mainShippingMethod,
    permission: PermissionEn.create
  },
  {
    path: `${accountingPath.mainShippingMethodEdit}/:id`,
    Layout: NewLayout,
    Component: MainShippingMethodForm,
    checkPath: accountingPath.mainShippingMethod,
    permission: PermissionEn.update
  },
  {
    path: `${accountingPath.mainShippingMethodDetail}/:id`,
    Layout: NewLayout,
    Component: MainShippingMethodForm,
    checkPath: accountingPath.mainShippingMethod,
    permission: PermissionEn.viewDetail
  },

  // Shipping stage
  {
    path: accountingPath.shippingStage,
    Layout: NewLayout,
    Component: ShippingStage,
    permission: PermissionEn.read
  },
  {
    path: accountingPath.shippingStageAdd,
    Layout: NewLayout,
    Component: ShippingStageForm,
    checkPath: accountingPath.shippingStage,
    permission: PermissionEn.create
  },
  {
    path: `${accountingPath.shippingStageEdit}/:id`,
    Layout: NewLayout,
    Component: ShippingStageForm,
    checkPath: accountingPath.shippingStage,
    permission: PermissionEn.update
  },
  {
    path: `${accountingPath.shippingStageDetail}/:id`,
    Layout: NewLayout,
    Component: ShippingStageForm,
    checkPath: accountingPath.shippingStage,
    permission: PermissionEn.viewDetail
  },

  // International Trade Agreement - ĐIỀU ƯỚC VÀ PHÁP LUẬT QUỐC TẾ
  {
    path: accountingPath.internationalTradeAgreement,
    Layout: NewLayout,
    Component: InternationalTradeAgreement,
    permission: PermissionEn.read
  },
  {
    path: accountingPath.internationalTradeAgreementAdd,
    Layout: NewLayout,
    Component: InternationalTradeAgreementForm,
    checkPath: accountingPath.internationalTradeAgreement,
    permission: PermissionEn.create
  },
  {
    path: `${accountingPath.internationalTradeAgreementEdit}/:id`,
    Layout: NewLayout,
    Component: InternationalTradeAgreementForm,
    checkPath: accountingPath.internationalTradeAgreement,
    permission: PermissionEn.update
  },
  {
    path: `${accountingPath.internationalTradeAgreementDetail}/:id`,
    Layout: NewLayout,
    Component: InternationalTradeAgreementForm,
    checkPath: accountingPath.internationalTradeAgreement,
    permission: PermissionEn.viewDetail
  },

  // Payment document detail requirement
  {
    path: accountingPath.paymentDocumentDetailRequirement,
    Layout: NewLayout,
    Component: PaymentDocumentDetailRequirement,
    permission: PermissionEn.read
  },
  {
    path: accountingPath.paymentDocumentDetailRequirementAdd,
    Layout: NewLayout,
    Component: PaymentDocumentDetailRequirementForm,
    checkPath: accountingPath.paymentDocumentDetailRequirement,
    permission: PermissionEn.create
  },
  {
    path: `${accountingPath.paymentDocumentDetailRequirementEdit}/:id`,
    Layout: NewLayout,
    Component: PaymentDocumentDetailRequirementForm,
    checkPath: accountingPath.paymentDocumentDetailRequirement,
    permission: PermissionEn.update
  },
  {
    path: `${accountingPath.paymentDocumentDetailRequirementDetail}/:id`,
    Layout: NewLayout,
    Component: PaymentDocumentDetailRequirementForm,
    checkPath: accountingPath.paymentDocumentDetailRequirement,
    permission: PermissionEn.viewDetail
  },

  //asset-allocation
  {
    path: accountingPath.assetAllocation,
    Layout: NewLayout,
    Component: AssetAllocation
  },
  {
    path: `${accountingPath.assetAllocationDetail}/:id`,
    Layout: NewLayout,
    Component: AssetAllocationForm
  },
  {
    path: accountingPath.assetAllocationAdd,
    Layout: NewLayout,
    Component: AssetAllocationForm
  },

  // Debt paying
  {
    path: accountingPath.debtPaying,
    Layout: NewLayout,
    Component: Debt
  },
  // Debt taking
  {
    path: accountingPath.debtTaking,
    Layout: NewLayout,
    Component: Debt
  },
  // Transaction master
  {
    path: accountingPath.transactionMaster,
    Layout: NewLayout,
    Component: TransactionMaster
  },
  {
    path: accountingPath.transactionMasterAdd,
    Layout: NewLayout,
    Component: TransactionMasterForm
  },
  {
    path: `${accountingPath.transactionMasterEdit}/:id`,
    Layout: NewLayout,
    Component: TransactionMasterForm
  },
  {
    path: `${accountingPath.transactionMasterDetail}/:id`,
    Layout: NewLayout,
    Component: TransactionMasterForm
  },
  // Document log
  {
    path: accountingPath.documentLog,
    Layout: NewLayout,
    Component: DocumentLog
  }
];

export default accountingRoutes;
